// Recruiting — the same-name review list (owner, 2026-10-11; PROGRESS.md
// item E). OWNER/DIRECTOR only: the board only draws the tab for them and the
// server refuses everyone else (src/recruiting_cleanup.js).
//
// Groups of records whose first and last name are EXACTLY the same once case
// and spaces are ignored. Rule 8: a shared name is never a match by itself —
// the records sit side by side and a person decides: Merge (the existing
// owner/director merge, lib/recruitingWrite.ts mergeCandidate) keeps the
// record marked "Keep" and folds another into it, or "Not the same person"
// answers the whole group and it stops showing (remembered on the server,
// until a NEW record with that name arrives). Groups whose records also share
// an email, phone, license or resume file are flagged first.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { useResource, refetchResource } from '../../lib/store';
import { EmptyState, FaultState } from '../FaultState';
import { LOCATION_NAMES, type LocationCode } from '../../lib/locations';
import {
  RECRUITING_SAME_NAME_KEY, fetchSameNameGroups, formatCivilDate, STAGE_LABEL,
  type SameNameGroup, type SameNameMember,
} from '../../lib/recruitingApi';
import { mergeCandidate, dismissSameNameGroup } from '../../lib/recruitingWrite';
import { useWrite } from '../MoneyForms';

const EM_DASH = '—';

const SHARED_WORD: Record<string, string> = {
  EMAIL: 'email',
  PHONE: 'phone',
  LICENSE: 'license',
  RESUME_SHA: 'resume file',
};

function clinicName(code: string | null): string {
  if (!code) return 'No clinic';
  return LOCATION_NAMES[code as LocationCode] ?? code;
}

function fullName(m: SameNameMember): string {
  return [m.firstName, m.middleName, m.lastName].filter((x) => x && String(x).trim()).join(' ');
}

/** Groups that share an email/phone/license/resume first, then by name. */
export function orderGroups(groups: SameNameGroup[]): SameNameGroup[] {
  return [...groups].sort((a, b) => {
    const sa = a.sharedIdentities.length > 0 ? 0 : 1;
    const sb = b.sharedIdentities.length > 0 ? 0 : 1;
    if (sa !== sb) return sa - sb;
    return a.nameKey < b.nameKey ? -1 : a.nameKey > b.nameKey ? 1 : 0;
  });
}

function GroupCard(
  { group, onOpen, onChanged }:
  { group: SameNameGroup; onOpen: (id: number) => void; onChanged: () => void },
): JSX.Element {
  // Default keeper: the record with the most applications, then the oldest.
  const defaultKeep = [...group.members].sort((a, b) => (b.applications - a.applications) || (a.id - b.id))[0]?.id ?? null;
  const [keepId, setKeepId] = useState<number | null>(defaultKeep);
  const [reason, setReason] = useState('');
  const write = useWrite();
  const sharedWith = (id: number): string[] => group.sharedIdentities
    .filter((s) => s.candidateIds.includes(id))
    .map((s) => SHARED_WORD[s.kind] ?? s.kind);

  function merge(duplicateId: number): void {
    if (keepId === null || keepId === duplicateId) return;
    const why = reason.trim() || 'Same person — same-name review';
    void write.run(async () => { await mergeCandidate(keepId, duplicateId, why); }, onChanged);
  }

  function notSame(): void {
    void write.run(async () => {
      await dismissSameNameGroup(group.members.map((m) => m.id), reason.trim() || undefined);
    }, onChanged);
  }

  const title = group.members[0] ? `${group.members[0].firstName} ${group.members[0].lastName}` : group.nameKey;

  return (
    <section class="card" data-testid={`same-name-group-${group.nameKey}`}>
      <div class="section-head">
        <div class="section-head-row">
          <h3>{title} <span class="muted">· {group.members.length} records</span></h3>
          {group.sharedIdentities.length > 0 ? (
            <span class="badge bad" data-testid="same-name-shared">
              Also share {[...new Set(group.sharedIdentities.map((s) => SHARED_WORD[s.kind] ?? s.kind))].join(', ')}
            </span>
          ) : <span class="badge">Name only</span>}
        </div>
      </div>
      <div class="table-wrap">
        <table class="data">
          <thead>
            <tr>
              <th class="name">Record</th>
              <th>Keep</th>
              <th>Clinic</th>
              <th>Email</th>
              <th>Phone</th>
              <th>License</th>
              <th>Stage</th>
              <th>Applications</th>
              <th>Resumes</th>
              <th>Last touch</th>
              <th>Merge</th>
            </tr>
          </thead>
          <tbody>
            {group.members.map((m) => {
              const shared = sharedWith(m.id);
              return (
                <tr key={m.id} data-testid={`same-name-member-${m.id}`} class={m.doNotContact ? 'dnc' : undefined}>
                  <td class="name">
                    <a
                      class="candidate-link" href={`/recruiting?candidate=${m.id}`}
                      onClick={(e: MouseEvent) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        onOpen(m.id);
                      }}
                    >
                      {fullName(m)} (#{m.id})
                    </a>
                    {m.doNotContact ? <span class="badge bad">Do not contact</span> : null}
                    {shared.length ? <span class="badge" title="shared with another record here">{shared.join(', ')}</span> : null}
                  </td>
                  <td>
                    <input
                      type="radio" name={`keep-${group.nameKey}`} aria-label={`Keep #${m.id}`}
                      checked={keepId === m.id} onChange={() => setKeepId(m.id)}
                      data-testid={`same-name-keep-${m.id}`}
                    />
                  </td>
                  <td>{clinicName(m.homeLocationCode)}</td>
                  <td>{m.email || m.emailRelay || EM_DASH}</td>
                  <td>{m.phone || m.phoneAlt || EM_DASH}</td>
                  <td>{m.licenseNumber || EM_DASH}</td>
                  <td>{m.outcome ? m.outcome : (STAGE_LABEL[m.stage] ?? m.stage)}</td>
                  <td>{m.applications}</td>
                  <td>{m.resumes}</td>
                  <td>{formatCivilDate(m.lastTouchAt)}</td>
                  <td>
                    {keepId !== null && keepId !== m.id ? (
                      <button
                        type="button" class="btn" disabled={write.busy}
                        onClick={() => merge(m.id)} data-testid={`same-name-merge-${m.id}`}
                      >
                        Merge into #{keepId}
                      </button>
                    ) : <span class="muted">kept</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div class="money-add">
        <input
          type="text" aria-label="Why (optional)" placeholder="Why (optional)" value={reason} disabled={write.busy}
          onInput={(e: Event) => setReason((e.target as HTMLInputElement).value)}
        />
        <button
          type="button" class="btn" disabled={write.busy} onClick={notSame}
          data-testid={`same-name-dismiss-${group.nameKey}`}
        >
          Not the same person
        </button>
      </div>
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </section>
  );
}

export function SameNameReview({ onOpenCandidate }: { onOpenCandidate: (id: number) => void }): JSX.Element {
  const load = useResource(RECRUITING_SAME_NAME_KEY, fetchSameNameGroups);
  const refresh = (): void => { void refetchResource(RECRUITING_SAME_NAME_KEY, fetchSameNameGroups); };

  if (load.status === 'loading') return <EmptyState label="Loading same-name records…" />;
  if (load.status === 'fault') return <FaultState name="recruiting_same_name_load_failed" detail={load.error} />;

  const data = load.data;
  const groups = orderGroups(data.groups);
  const sharing = groups.filter((g) => g.sharedIdentities.length > 0).length;

  return (
    <div data-testid="same-name-review">
      <p class="muted" data-testid="same-name-count">
        {data.totalGroups} name{data.totalGroups === 1 ? '' : 's'} on {data.totalRecords} records
        {sharing ? ` · ${sharing} also share an email, phone, license or resume` : ''}
        {data.dismissedGroups ? ` · ${data.dismissedGroups} answered "not the same person"` : ''}
        {data.truncated ? ` · showing the first ${data.groups.length}` : ''}
      </p>
      <p class="muted">
        Same first and last name only (capitals and spaces ignored) — a name alone never proves it is one person.
        Pick the record to keep, then Merge the others into it, or answer Not the same person.
      </p>
      {groups.length === 0 ? <EmptyState label="No same-name records left to review." /> : (
        groups.map((g) => (
          <GroupCard key={`${g.nameKey}:${g.candidateIds.join(',')}`} group={g} onOpen={onOpenCandidate} onChanged={refresh} />
        ))
      )}
    </div>
  );
}
